import { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import axios from "axios";
import { KeyRound, Save, RotateCcw, Activity, AlertTriangle, CheckCircle2, Eye, EyeOff, Truck, Mail } from "lucide-react";
import { toast } from "sonner";
import { API } from "../../lib/api";
import { EcommerceToggleCard } from "../../components/EcommerceToggleCard";

const AdminSettings = () => {
  const { authHeaders } = useOutletContext();
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newKey, setNewKey] = useState("");
  const [showKey, setShowKey] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const load = () => {
    setLoading(true);
    axios
      .get(`${API}/admin/settings/mollie`, { headers: authHeaders })
      .then((r) => setInfo(r.data))
      .catch(() => toast.error("Instellingen laden mislukt"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authHeaders]);

  const save = async () => {
    const trimmed = newKey.trim();
    if (!trimmed) {
      toast.error("Vul een Mollie API key in.");
      return;
    }
    if (!(trimmed.startsWith("live_") || trimmed.startsWith("test_"))) {
      toast.error("Mollie key moet beginnen met 'live_' of 'test_'.");
      return;
    }
    setSaving(true);
    setTestResult(null);
    try {
      const res = await axios.put(
        `${API}/admin/settings/mollie`,
        { api_key: trimmed },
        { headers: authHeaders }
      );
      toast.success(`Mollie key opgeslagen (${res.data.mode}-mode)`);
      setNewKey("");
      setTestResult({
        ok: true,
        methods: res.data.methods_available || [],
      });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Opslaan mislukt");
      setTestResult({ ok: false, error: e?.response?.data?.detail || "Fout" });
    } finally {
      setSaving(false);
    }
  };

  const clearOverride = async () => {
    if (!window.confirm("Weet je zeker dat je terug wilt naar de standaard key (uit .env)?")) return;
    try {
      await axios.delete(`${API}/admin/settings/mollie`, { headers: authHeaders });
      toast.success("Terug naar standaard key");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Verwijderen mislukt");
    }
  };

  const testConnection = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const res = await axios.post(
        `${API}/admin/settings/mollie/test`,
        {},
        { headers: authHeaders }
      );
      toast.success("Verbinding met Mollie OK");
      setTestResult({ ok: true, methods: res.data.methods_available || [] });
    } catch (e) {
      const msg = e?.response?.data?.detail || "Test mislukt";
      toast.error(msg);
      setTestResult({ ok: false, error: msg });
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <div data-testid="admin-settings-loading" className="text-[var(--tha-muted)] font-mono-x text-sm">
        Laden…
      </div>
    );
  }

  return (
    <div data-testid="admin-settings" className="space-y-8">
      <div>
        <div className="font-display text-3xl">INSTELLINGEN</div>
        <div className="text-sm font-mono-x text-[var(--tha-muted)] mt-1">
          Beheer betaalprovider keys en andere systeeminstellingen.
        </div>
      </div>

      <EcommerceToggleCard authHeaders={authHeaders} />

      {/* Mollie card */}
      <div className="border border-[var(--tha-border)] bg-[#0a0a0a] p-6 sm:p-8">
        <div className="flex items-center gap-3 mb-6">
          <KeyRound size={18} className="text-[var(--tha-red)]" />
          <div className="font-display text-2xl">MOLLIE API KEY</div>
        </div>

        {/* Current status */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-px bg-[var(--tha-border)] mb-6">
          <StatusCell label="ACTIEVE KEY" value={info?.has_key ? info.masked : "GEEN"} />
          <StatusCell
            label="MODUS"
            value={info?.mode ? info.mode.toUpperCase() : "—"}
            accent={info?.mode === "live"}
          />
          <StatusCell
            label="BRON"
            value={
              info?.source === "db"
                ? "ADMIN PANEL"
                : info?.source === "env"
                ? "ENV FILE"
                : "GEEN"
            }
          />
        </div>

        {/* Last error */}
        {info?.last_error && (
          <div
            data-testid="mollie-last-error"
            className="border border-[var(--tha-red)] bg-[#1a0000] p-4 mb-6"
          >
            <div className="flex items-center gap-2 text-[var(--tha-red)] text-xs font-mono-x tracking-widest mb-2">
              <AlertTriangle size={14} /> LAATSTE MOLLIE-FOUT
            </div>
            <div className="text-sm text-white font-mono-x break-all">
              {info.last_error.error}
            </div>
            <div className="text-xs text-[var(--tha-muted)] font-mono-x mt-2">
              {info.last_error.type} · {info.last_error.at}
            </div>
          </div>
        )}

        {/* Form */}
        <div className="space-y-4">
          <div>
            <label className="text-[10px] font-mono-x tracking-widest text-[var(--tha-muted)] block mb-2">
              NIEUWE MOLLIE API KEY
            </label>
            <div className="flex gap-2">
              <div className="flex-1 relative">
                <input
                  data-testid="mollie-key-input"
                  type={showKey ? "text" : "password"}
                  value={newKey}
                  onChange={(e) => setNewKey(e.target.value)}
                  placeholder="live_xxxxxxxxxxxxxx of test_xxxxxxxxxxxxxx"
                  className="w-full bg-[#050505] border border-[var(--tha-border)] px-4 py-3 text-sm font-mono-x focus:outline-none focus:border-[var(--tha-red)]"
                />
                <button
                  type="button"
                  onClick={() => setShowKey((s) => !s)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--tha-muted)] hover:text-white"
                  data-testid="mollie-key-toggle-visibility"
                >
                  {showKey ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
              <button
                data-testid="mollie-key-save"
                onClick={save}
                disabled={saving || !newKey}
                className="bg-[var(--tha-red)] text-black font-mono-x tracking-widest text-xs px-5 py-3 hover:opacity-90 disabled:opacity-40 inline-flex items-center gap-2"
              >
                <Save size={14} /> {saving ? "OPSLAAN…" : "OPSLAAN"}
              </button>
            </div>
            <div className="text-xs font-mono-x text-[var(--tha-muted)] mt-2">
              De key wordt eerst getest tegen Mollie voordat hij wordt opgeslagen.
              Je vindt je key op{" "}
              <a
                href="https://my.mollie.com/dashboard/developers/api-keys"
                target="_blank"
                rel="noreferrer"
                className="text-[var(--tha-red)] underline"
              >
                Mollie Dashboard → Developers → API keys
              </a>
              .
            </div>
          </div>

          <div className="flex flex-wrap gap-3 pt-2">
            <button
              data-testid="mollie-test-connection"
              onClick={testConnection}
              disabled={testing || !info?.has_key}
              className="border border-[var(--tha-border)] hover:border-white text-white font-mono-x tracking-widest text-xs px-4 py-2.5 inline-flex items-center gap-2 disabled:opacity-40"
            >
              <Activity size={14} /> {testing ? "TESTEN…" : "TEST VERBINDING"}
            </button>
            {info?.source === "db" && (
              <button
                data-testid="mollie-reset-to-env"
                onClick={clearOverride}
                className="border border-[var(--tha-border)] hover:border-white text-[var(--tha-muted)] hover:text-white font-mono-x tracking-widest text-xs px-4 py-2.5 inline-flex items-center gap-2"
              >
                <RotateCcw size={14} /> TERUG NAAR .ENV KEY
              </button>
            )}
          </div>

          {/* Test result */}
          {testResult && (
            <div
              data-testid="mollie-test-result"
              className={`mt-4 border p-4 ${
                testResult.ok
                  ? "border-green-700 bg-green-900/10"
                  : "border-[var(--tha-red)] bg-[#1a0000]"
              }`}
            >
              {testResult.ok ? (
                <>
                  <div className="flex items-center gap-2 text-green-400 text-xs font-mono-x tracking-widest mb-2">
                    <CheckCircle2 size={14} /> VERBINDING OK
                  </div>
                  <div className="text-xs font-mono-x text-[var(--tha-muted)] mb-1">
                    ACTIEVE BETAALMETHODES:
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {testResult.methods.length === 0 ? (
                      <span className="text-sm text-[var(--tha-muted)]">
                        Geen methodes actief
                      </span>
                    ) : (
                      testResult.methods.map((m) => (
                        <span
                          key={m}
                          className="text-xs font-mono-x text-white border border-[var(--tha-border)] px-2 py-1"
                        >
                          {m}
                        </span>
                      ))
                    )}
                  </div>
                  <div className="text-xs font-mono-x text-[var(--tha-muted)] mt-3">
                    Mist een methode (bv. Klarna)? Activeer hem in je Mollie
                    Dashboard → Settings → Profiles → Payment methods.
                  </div>
                </>
              ) : (
                <>
                  <div className="flex items-center gap-2 text-[var(--tha-red)] text-xs font-mono-x tracking-widest mb-2">
                    <AlertTriangle size={14} /> FOUT
                  </div>
                  <div className="text-sm text-white font-mono-x break-all">
                    {testResult.error}
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="text-xs font-mono-x text-[var(--tha-muted)] leading-relaxed">
        Tip: na het opslaan is de nieuwe key direct actief op de preview-omgeving.
        Voor de productie-website (tikhemaanshot.nl) is geen redeploy nodig —
        de key wordt opgehaald uit de database bij elke bestelling.
      </div>

      <ShippingSettingsCard authHeaders={authHeaders} />
      <SmtpSettingsCard authHeaders={authHeaders} />
    </div>
  );
};

const SmtpSettingsCard = ({ authHeaders }) => {
  const [data, setData] = useState(null);
  const [form, setForm] = useState({ user: "", password: "", host: "", port: 587, sender_name: "" });
  const [showPw, setShowPw] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  const load = () => {
    axios
      .get(`${API}/admin/settings/smtp`, { headers: authHeaders })
      .then((r) => {
        setData(r.data);
        setForm({
          user: r.data.user || "",
          password: "",  // never prefill
          host: r.data.host || "smtp.gmail.com",
          port: r.data.port || 587,
          sender_name: r.data.sender_name || "Tik Hem Aan!",
        });
      })
      .catch(() => toast.error("SMTP-instellingen laden mislukt"));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authHeaders]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      if (!payload.password) delete payload.password;  // keep existing
      await axios.put(`${API}/admin/settings/smtp`, payload, { headers: authHeaders });
      toast.success("SMTP-instellingen opgeslagen");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Opslaan mislukt");
    } finally {
      setSaving(false);
    }
  };

  const test = async () => {
    setTesting(true);
    try {
      const res = await axios.post(`${API}/admin/settings/smtp/test`, {}, { headers: authHeaders });
      toast.success(`Test-mail verstuurd naar ${res.data.sent_to}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Testmail mislukt");
      load();
    } finally {
      setTesting(false);
    }
  };

  if (!data) return null;

  return (
    <div data-testid="admin-smtp-settings" className="border border-[var(--tha-border)] bg-[#0a0a0a] p-6 sm:p-8">
      <div className="flex items-center gap-3 mb-6">
        <Mail size={18} className="text-[var(--tha-red)]" />
        <div className="font-display text-2xl">E-MAIL (SMTP)</div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-px bg-[var(--tha-border)] mb-6">
        <StatusCell label="AFZENDER" value={data.user} />
        <StatusCell label="HUIDIG WACHTWOORD" value={data.password_masked || "—"} accent={data.has_password} />
        <StatusCell label="BRON" value={data.source === "db" ? "ADMIN PANEL" : "ENV FILE"} />
      </div>

      {data.last_error && (
        <div data-testid="smtp-last-error" className="border border-[var(--tha-red)] bg-[#1a0000] p-4 mb-6">
          <div className="flex items-center gap-2 text-[var(--tha-red)] text-xs font-mono-x tracking-widest mb-2">
            <AlertTriangle size={14} /> LAATSTE E-MAIL FOUT
          </div>
          <div className="text-sm text-white font-mono-x break-all">{data.last_error.error}</div>
          <div className="text-xs text-[var(--tha-muted)] font-mono-x mt-2">
            {data.last_error.at} · voor {data.last_error.to}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div>
          <label className="text-[10px] font-mono-x tracking-widest text-[var(--tha-muted)] block mb-1">GMAIL ADRES</label>
          <input
            data-testid="smtp-user-input"
            value={form.user}
            onChange={(e) => setForm((f) => ({ ...f, user: e.target.value }))}
            className="w-full bg-[#050505] border border-[var(--tha-border)] px-4 py-3 text-sm font-mono-x focus:outline-none focus:border-[var(--tha-red)]"
          />
        </div>
        <div>
          <label className="text-[10px] font-mono-x tracking-widest text-[var(--tha-muted)] block mb-1">
            APP-WACHTWOORD {data.has_password ? "(LEEG = HOUDEN)" : "(NIEUW)"}
          </label>
          <div className="relative">
            <input
              data-testid="smtp-password-input"
              type={showPw ? "text" : "password"}
              value={form.password}
              onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
              placeholder="Nieuw 16-tekens app-password"
              className="w-full bg-[#050505] border border-[var(--tha-border)] px-4 py-3 pr-10 text-sm font-mono-x focus:outline-none focus:border-[var(--tha-red)]"
            />
            <button
              type="button"
              onClick={() => setShowPw((s) => !s)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--tha-muted)] hover:text-white"
            >
              {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
        </div>
        <div>
          <label className="text-[10px] font-mono-x tracking-widest text-[var(--tha-muted)] block mb-1">AFZENDER NAAM</label>
          <input
            value={form.sender_name}
            onChange={(e) => setForm((f) => ({ ...f, sender_name: e.target.value }))}
            className="w-full bg-[#050505] border border-[var(--tha-border)] px-4 py-3 text-sm font-mono-x focus:outline-none focus:border-[var(--tha-red)]"
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-[10px] font-mono-x tracking-widest text-[var(--tha-muted)] block mb-1">HOST</label>
            <input
              value={form.host}
              onChange={(e) => setForm((f) => ({ ...f, host: e.target.value }))}
              className="w-full bg-[#050505] border border-[var(--tha-border)] px-4 py-3 text-sm font-mono-x focus:outline-none focus:border-[var(--tha-red)]"
            />
          </div>
          <div>
            <label className="text-[10px] font-mono-x tracking-widest text-[var(--tha-muted)] block mb-1">PORT</label>
            <input
              type="number"
              value={form.port}
              onChange={(e) => setForm((f) => ({ ...f, port: parseInt(e.target.value, 10) || 587 }))}
              className="w-full bg-[#050505] border border-[var(--tha-border)] px-4 py-3 text-sm font-mono-x focus:outline-none focus:border-[var(--tha-red)]"
            />
          </div>
        </div>
      </div>

      <div className="mt-6 flex flex-wrap gap-3">
        <button
          data-testid="smtp-save"
          onClick={save}
          disabled={saving}
          className="bg-[var(--tha-red)] text-black font-mono-x tracking-widest text-xs px-5 py-3 hover:opacity-90 disabled:opacity-40 inline-flex items-center gap-2"
        >
          <Save size={14} /> {saving ? "OPSLAAN…" : "OPSLAAN"}
        </button>
        <button
          data-testid="smtp-test"
          onClick={test}
          disabled={testing || !data.has_password}
          className="border border-[var(--tha-border)] hover:border-white text-white font-mono-x tracking-widest text-xs px-4 py-3 disabled:opacity-40 inline-flex items-center gap-2"
        >
          <Activity size={14} /> {testing ? "TESTEN…" : "STUUR TEST-MAIL"}
        </button>
      </div>

      <div className="mt-4 text-xs font-mono-x text-[var(--tha-muted)] leading-relaxed">
        Gmail vereist een <strong>app-wachtwoord</strong> (geen normaal wachtwoord). Genereer er één op{" "}
        <a href="https://myaccount.google.com/apppasswords" target="_blank" rel="noreferrer" className="text-[var(--tha-red)] underline">
          myaccount.google.com/apppasswords
        </a>{" "}
        (2FA moet aan staan). Bij het opslaan wordt hij hier veilig bewaard — geen redeploy nodig.
      </div>
    </div>
  );
};

const ShippingSettingsCard = ({ authHeaders }) => {
  const [data, setData] = useState({ flat_cost: 5.95, free_from: 60 });
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/admin/settings/shipping`, { headers: authHeaders })
      .then((r) => setData(r.data))
      .catch(() => toast.error("Verzendinstellingen laden mislukt"))
      .finally(() => setLoaded(true));
  }, [authHeaders]);

  const save = async () => {
    const flat = parseFloat(data.flat_cost);
    const free = parseFloat(data.free_from);
    if (isNaN(flat) || flat < 0) {
      toast.error("Vul geldige verzendkosten in.");
      return;
    }
    if (isNaN(free) || free < 0) {
      toast.error("Vul een geldige gratis-vanaf drempel in.");
      return;
    }
    setSaving(true);
    try {
      const res = await axios.put(
        `${API}/admin/settings/shipping`,
        { flat_cost: flat, free_from: free },
        { headers: authHeaders }
      );
      setData(res.data);
      toast.success("Verzendinstellingen opgeslagen");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Opslaan mislukt");
    } finally {
      setSaving(false);
    }
  };

  if (!loaded) return null;

  return (
    <div
      data-testid="admin-shipping-settings"
      className="border border-[var(--tha-border)] bg-[#0a0a0a] p-6 sm:p-8"
    >
      <div className="flex items-center gap-3 mb-6">
        <Truck size={18} className="text-[var(--tha-red)]" />
        <div className="font-display text-2xl">VERZENDKOSTEN</div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 max-w-2xl">
        <div>
          <label className="text-[10px] font-mono-x tracking-widest text-[var(--tha-muted)] block mb-2">
            VAST TARIEF PER PAKKET (€)
          </label>
          <input
            data-testid="shipping-flat-input"
            type="number"
            step="0.05"
            min="0"
            value={data.flat_cost}
            onChange={(e) => setData((d) => ({ ...d, flat_cost: e.target.value }))}
            className="w-full bg-[#050505] border border-[var(--tha-border)] px-4 py-3 text-sm font-mono-x focus:outline-none focus:border-[var(--tha-red)]"
          />
          <div className="text-[10px] font-mono-x text-[var(--tha-muted)] mt-1">
            Wordt op alle bestellingen onder de gratis-drempel gerekend.
          </div>
        </div>
        <div>
          <label className="text-[10px] font-mono-x tracking-widest text-[var(--tha-muted)] block mb-2">
            GRATIS VERZENDING VANAF (€)
          </label>
          <input
            data-testid="shipping-free-input"
            type="number"
            step="1"
            min="0"
            value={data.free_from}
            onChange={(e) => setData((d) => ({ ...d, free_from: e.target.value }))}
            className="w-full bg-[#050505] border border-[var(--tha-border)] px-4 py-3 text-sm font-mono-x focus:outline-none focus:border-[var(--tha-red)]"
          />
          <div className="text-[10px] font-mono-x text-[var(--tha-muted)] mt-1">
            Klanten zien dit op productpagina&apos;s en in het mandje. Nul = altijd vast tarief.
          </div>
        </div>
      </div>

      <div className="mt-6">
        <button
          data-testid="shipping-save"
          onClick={save}
          disabled={saving}
          className="bg-[var(--tha-red)] text-black font-mono-x tracking-widest text-xs px-5 py-3 hover:opacity-90 disabled:opacity-40 inline-flex items-center gap-2"
        >
          <Save size={14} /> {saving ? "OPSLAAN…" : "OPSLAAN"}
        </button>
      </div>
    </div>
  );
};

const StatusCell = ({ label, value, accent }) => (
  <div className={`bg-[#0a0a0a] p-4 ${accent ? "border-l-2 border-l-[var(--tha-red)]" : ""}`}>
    <div className="text-[10px] font-mono-x tracking-widest text-[var(--tha-muted)]">
      {label}
    </div>
    <div className="font-display text-xl mt-1 text-white break-all">{value}</div>
  </div>
);

export default AdminSettings;
